/**
 * Outcome refinement chat panel — creates or resumes a linked conversation
 * for AI-assisted outcome definition and test strategy development.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquare, ExternalLink, Loader2 } from 'lucide-react';
import { createOutcomeConversation } from '@/lib/api';

interface OutcomeChatPanelProps {
  codebaseId: string;
  outcomeId: string;
  outcomeTitle: string;
  existingConversationId: string | null;
}

export function OutcomeChatPanel({
  codebaseId,
  outcomeId,
  outcomeTitle,
  existingConversationId,
}: OutcomeChatPanelProps): React.ReactElement {
  const navigate = useNavigate();
  const [conversationId, setConversationId] = useState<string | null>(existingConversationId);

  const createMutation = useMutation({
    mutationFn: () => createOutcomeConversation(codebaseId, outcomeId),
    onSuccess: result => {
      setConversationId(result.conversationId);
      void navigate(`/chat/${result.conversationId}`);
    },
  });

  const handleOpenChat = (): void => {
    if (conversationId) {
      void navigate(`/chat/${conversationId}`);
    } else {
      createMutation.mutate();
    }
  };

  return (
    <Card className="border-border/70 bg-surface/95 shadow-sm">
      <CardHeader className="px-4 py-4">
        <CardTitle className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            <span>AI Refinement</span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 px-4 pb-4">
        <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-3">
          <p className="text-xs leading-5 text-text-secondary">
            Collaborate with AI to refine acceptance criteria, identify scope gaps, and develop test
            strategies for <span className="font-medium text-text-primary">{outcomeTitle}</span>.
          </p>
        </div>

        {conversationId ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-xl border border-success/20 bg-success/5 px-3 py-2 text-xs text-text-tertiary">
              <div className="h-2 w-2 rounded-full bg-success" />
              <span>Conversation linked</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenChat}
              className="h-9 w-full rounded-xl text-xs"
            >
              <ExternalLink className="mr-1.5 h-3 w-3" />
              Continue Refinement
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            onClick={handleOpenChat}
            disabled={createMutation.isPending}
            className="h-9 w-full rounded-xl text-xs"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <MessageSquare className="mr-1.5 h-3 w-3" />
                Refine with AI
              </>
            )}
          </Button>
        )}

        {createMutation.isError && (
          <div className="text-xs text-error">
            {createMutation.error instanceof Error
              ? createMutation.error.message
              : 'Failed to start conversation'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
